import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { usePageTone } from '@/components/generals/tone'
import { LocalTime } from '@/components/generals/local-time'
import { RevealLines } from '@/components/motion/reveal-lines'
import { ClipReveal } from '@/components/motion/clip-reveal'
import { Photo } from '@/components/motion/photo'
import { EnquiryForm } from '@/components/contact/enquiry-form'
import { formatPlate, pick } from '@/content/photographs'
import { profile, socials } from '@/content/profile'

const hoopoe = pick('hoopoe-in-the-grass')

const Contact: FC = (): ReactNode => {
    usePageTone('#0e0e0b', 'dark')

    return (
        <Layout>
            <Seo
                title='Contact'
                description='Enquiries for Arian Mavriqi: prints, image licensing, wildlife assignments, talks and exhibitions. Send a message from his website.'
            />

            <section aria-labelledby='contact-title' className='px-5 pb-[16vh] pt-[22vh] md:px-8 md:pt-[24vh]'>
                <div className='grid gap-y-16 md:grid-cols-12 md:gap-x-8'>
                    <div className='md:col-span-5'>
                        <p className='font-mono text-[10px] uppercase tracking-[0.22em] text-fog'>
                            Enquiries
                        </p>

                        <RevealLines
                            as='h1'
                            id='contact-title'
                            lines={['Prints, licences,', <span key='talks' className='italic'>talks &amp; fieldwork.</span>]}
                            delay={0.35}
                            className='mt-6 font-heading text-[clamp(48px,6.2vw,108px)] leading-[0.92] tracking-[-0.04em] text-bone display-soft'
                        />

                        <p className='mt-8 max-w-[42ch] text-[17px] leading-[1.65] text-bone/70'>
                            Enquiries about prints, image licensing, documentation work, talks or exhibitions are welcome. Write a few lines below and your message arrives by email, with your address ready for the reply.
                        </p>

                        <dl className='mt-12 grid grid-cols-2 gap-8 font-mono text-[10px] uppercase tracking-[0.2em]'>
                            <div>
                                <dt className='text-fog'>Based in</dt>
                                <dd className='mt-2 text-[12px] normal-case tracking-normal text-bone'>
                                    {profile.baseLocal}, Kosovo
                                    <br />
                                    <span className='text-fog'>{profile.coordinates}</span>
                                </dd>
                            </div>
                            <div>
                                <dt className='text-fog'>Local time</dt>
                                <dd className='mt-2 text-[12px] tracking-normal text-bone'>
                                    <LocalTime />
                                </dd>
                            </div>
                            <div className='col-span-2'>
                                <dt className='text-fog'>Elsewhere</dt>
                                <dd className='mt-3'>
                                    <ul className='flex flex-wrap gap-x-6 gap-y-3'>
                                        {socials.map(social => (
                                            <li key={social.href}>
                                                <a
                                                    href={social.href}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                    className='border-b border-bone/25 pb-0.5 text-bone transition-colors hover:border-ember hover:text-ember'
                                                >
                                                    {social.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </dd>
                            </div>
                        </dl>

                        <figure className='mt-16 hidden w-[62%] md:block'>
                            <ClipReveal from='bottom'>
                                <div style={{ backgroundColor: hoopoe.tone }}>
                                    <Photo photo={hoopoe} sizes='26vw' />
                                </div>
                            </ClipReveal>
                            <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-fog'>
                                Pl. {formatPlate(hoopoe.plate)} &mdash; {hoopoe.title}, his favourite subject
                            </figcaption>
                        </figure>
                    </div>

                    <div className='md:col-span-6 md:col-start-7 md:pt-[18vh]'>
                        <EnquiryForm />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default Contact
